// Resultaten-paneel voor het geselecteerde meetvlak — totaal schaduwuren,
// drempelverdeling 0/0–33/33–66/66–100/100, mini-staafdiagram per maand,
// en een knop voor de Haagse-norm-toets (zie §2.5 / §3.4).
//
// Bevat geen bereken-knop meer; die staat altijd bovenaan in BlockPanel
// (AnalysisActions). Wanneer er nog geen resultaat is rendert dit paneel
// niets — de bovenste knop is dan de aangewezen plek voor de gebruiker.

import { useState } from "react";
import { Calculator, Scale } from "lucide-react";
import { useStore } from "../state/store";
import {
  hashScenarioForShadow,
  type SurfaceResult,
} from "../shadow";
import { Button } from "./ui/Button";
import { InfoBadge } from "./ui/InfoBadge";
import { evaluateHaagseNormForBlock, type HaagseNormReport } from "../shadow/haagseNorm";

interface Props {
  /** ID van het meetvlak (zie detectMeasurementSurfaces). */
  surfaceId: string;
}

export function ResultsPanel({ surfaceId }: Props) {
  const yearResult = useStore((s) => s.yearResult);
  const yearResultHash = useStore((s) => s.yearResultHash);
  const scenario = useStore((s) => s.scenario);

  const surface: SurfaceResult | undefined = yearResult?.surfaces.find((s) => s.surfaceId === surfaceId);
  const currentHash = hashScenarioForShadow(scenario);
  const stale = yearResult !== null && yearResultHash !== null && currentHash !== yearResultHash;

  if (!surface) return null;

  return (
    <section>
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
        <Calculator size={14} /> Jaarstatistieken
      </h3>

      {stale && (
        <div className="mb-2 p-2 rounded-md bg-amber-100 text-amber-900 text-xs">
          Resultaten zijn verouderd — geometrie gewijzigd. Klik bovenaan op
          "Herbereken jaarschaduw" om de cijfers te actualiseren.
        </div>
      )}

      <div className="text-xs space-y-1.5 mb-3">
        <Stat
          label="Totaal schaduwuren / jaar"
          value={`${surface.avgShadowHours.toFixed(0)} u`}
        />
        <Stat
          label="Aandeel van daglichturen"
          value={`${surface.avgShadowPercent.toFixed(1)} %`}
        />
        <Stat
          label="Daglichturen totaal"
          value={`${surface.totalDaylightHours.toFixed(0)} u`}
        />
        <Stat
          label="Cellen op meetvlak"
          value={`${surface.cellCount} (${surface.cellResolutionM} m)`}
        />
      </div>

      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">
        Verdeling
      </h4>
      <BucketBars surface={surface} />

      <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-3 mb-1.5">
        Per maand
      </h4>
      <MonthBars surface={surface} />

      <HaagseNormToggle blockId={surface.blockId} />
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function BucketBars({ surface }: { surface: SurfaceResult }) {
  const total = Math.max(1, surface.cellCount);
  const items: { label: string; count: number; color: string }[] = [
    { label: "0%", count: surface.buckets.bucket0, color: "bg-emerald-500" },
    { label: "0–33%", count: surface.buckets.bucket0_33, color: "bg-lime-500" },
    { label: "33–66%", count: surface.buckets.bucket33_66, color: "bg-amber-500" },
    { label: "66–100%", count: surface.buckets.bucket66_100, color: "bg-orange-500" },
    { label: "100%", count: surface.buckets.bucket100, color: "bg-rose-600" },
  ];
  return (
    <div className="space-y-1">
      {items.map((it) => {
        const pct = (it.count / total) * 100;
        return (
          <div key={it.label} className="flex items-center gap-2 text-xs">
            <span className="w-14 text-muted-foreground">{it.label}</span>
            <div className="flex-1 h-3 bg-secondary rounded overflow-hidden">
              <div
                className={"h-full " + it.color}
                style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
              />
            </div>
            <span className="w-12 text-right font-mono text-muted-foreground">
              {pct.toFixed(0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

const MONTHS = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

function MonthBars({ surface }: { surface: SurfaceResult }) {
  // Schaal: gebruik observed max, maar minimaal 10%. Dit voorkomt dat bij
  // weinig schaduw (bv. alle maanden < 3%) een staaf van 2,8% de hele chart
  // vult en daardoor naast een buur van 0% disproportioneel dramatisch oogt.
  // Bij meetvlakken met substantiële schaduw (max ≥ 10%) gedraagt de chart
  // zich identiek aan voor: de hoogste maand vult de chart.
  const observedMax = Math.max(0, ...surface.monthly.map((m) => m.avgShadowFraction));
  const max = Math.max(0.10, observedMax);
  const CHART_PX = 72;

  const yTicks = [max, max / 2, 0];
  const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;

  return (
    <div className="flex gap-1" style={{ height: CHART_PX + 14 }}>
      {/* Y-as: 3 labels op 100%, 50%, 0% van de schaal. justify-between
          plaatst ze respectievelijk op chart-top, midden, chart-bottom. */}
      <div
        className="flex flex-col justify-between text-[9px] text-muted-foreground tabular-nums pr-1"
        style={{ height: CHART_PX }}
      >
        {yTicks.map((v, i) => (
          <span key={i} className="leading-none">{fmtPct(v)}</span>
        ))}
      </div>

      {/* Chart-gebied. position:relative voor de grid-lijnen-overlay. */}
      <div className="relative flex-1 flex flex-col">
        {/* Horizontale gridlijnen op 0%, 50%, 100% van de chart-hoogte. */}
        <div
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{ height: CHART_PX }}
          aria-hidden
        >
          <div className="absolute inset-x-0 border-t border-border" style={{ top: 0 }} />
          <div className="absolute inset-x-0 border-t border-border opacity-60" style={{ top: "50%" }} />
          <div className="absolute inset-x-0 border-t border-border" style={{ top: "100%" }} />
        </div>

        <div className="relative flex items-stretch gap-1">
          {surface.monthly.map((m) => {
            const h = (m.avgShadowFraction / max) * 100;
            return (
              <div
                key={m.month}
                className="flex-1 flex flex-col items-stretch"
                title={`${MONTHS[m.month - 1]}: ${fmtPct(m.avgShadowFraction)} schaduw`}
              >
                <div className="flex items-end" style={{ height: CHART_PX }}>
                  <div
                    className="w-full bg-blue-600 rounded-sm"
                    style={{ height: `${h}%`, minHeight: m.avgShadowFraction > 0 ? "2px" : "0" }}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground leading-none text-center mt-0.5">
                  {MONTHS[m.month - 1].charAt(0)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HaagseNormToggle({ blockId }: { blockId: string }) {
  const [open, setOpen] = useState(false);
  const scenario = useStore((s) => s.scenario);

  const block = scenario.blocks.find((b) => b.id === blockId);
  let report: HaagseNormReport | null = null;
  if (open && block) {
    try {
      report = evaluateHaagseNormForBlock(scenario, block);
    } catch (err) {
      console.error("[HaagseNorm]", err);
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => setOpen((v) => !v)}
      >
        <Scale size={14} /> {open ? "Verberg" : "Toets aan"} Haagse norm
      </Button>
      {open && report && <HaagseNormCard report={report} />}
      <div className="text-[11px] text-muted-foreground mt-2 leading-snug flex items-start gap-1.5">
        <InfoBadge text="Lichte toets (19/2 + 21/10): ≥ 2 uur ononderbroken zon op het meetpunt tussen 10:00 en 18:00. Strenge toets (19/1 + 22/11): ≥ 3 uur — strengere datums én strengere drempel." />
        <span>
          Lichte toets ≥ 2 u (19/2 + 21/10), strenge toets ≥ 3 u (19/1 + 22/11),
          tussen 10:00–18:00.
        </span>
      </div>
    </div>
  );
}

function HaagseNormCard({ report }: { report: HaagseNormReport }) {
  return (
    <div className="mt-2 text-xs space-y-2">
      {report.checks.map((c) => {
        const requiredHours = c.requiredMinutes / 60;
        return (
          <div
            key={c.label}
            className={
              "p-2 rounded-md border " +
              (c.passes ? "border-emerald-500 bg-emerald-50" : "border-rose-500 bg-rose-50")
            }
          >
            <div className="font-medium">{c.label}</div>
            <div className="text-muted-foreground">Datum: {c.dateLabel}</div>
            <div>
              Langste ononderbroken zonperiode op het meetpunt:{" "}
              <span className="font-mono">
                {(c.longestSunlitMinutes / 60).toFixed(2)} u
              </span>{" "}
              ({c.passes ? "✔ voldoet" : "✘ voldoet niet"} aan ≥
              {requiredHours} u)
            </div>
          </div>
        );
      })}
    </div>
  );
}
